import { MAX_ROWS } from './lengths';

/**
 * Aggregate game stats (spec §3.8). Distribution index = guessCount - 1,
 * with a bucket for every row a 7-letter game can use (spec §10.1), so a
 * win in 7 or 8 has somewhere to go.
 */
export interface Stats {
  played: number;
  won: number;
  distribution: readonly number[];
}

export const EMPTY_STATS: Stats = Object.freeze({
  played: 0,
  won: 0,
  distribution: Object.freeze(new Array(MAX_ROWS).fill(0)),
});

/**
 * Grow a stored distribution to the current bucket count (spec §10.6).
 * Stats written before the ladder have six buckets; padding on load is
 * what keeps them valid instead of silently resetting a player's history.
 * Always returns a fresh array.
 */
export function padDistribution(stored: readonly number[]): number[] {
  const out = [...stored];
  while (out.length < MAX_ROWS) out.push(0);
  return out;
}

/** Pure: record a finished game (daily or challenge). */
export function recordGame(stats: Stats, won: boolean, guessCount: number): Stats {
  const distribution = [...stats.distribution];
  if (won) distribution[guessCount - 1] = (distribution[guessCount - 1] ?? 0) + 1;
  return { played: stats.played + 1, won: stats.won + (won ? 1 : 0), distribution };
}

/** Integer win percentage, 0 when nothing played. */
export function winPercent(stats: Stats): number {
  return stats.played === 0 ? 0 : Math.round((stats.won / stats.played) * 100);
}
